import { ts } from '../types';

/** A namespace containing useful filters to be passed to `Array.filter()` or in if-statements. */
export namespace Filters {
    /** Returns `true` if `x !== null | undefined`
     *
     * ### Example
     * ```typescript
     * const myArr: Array<string | null | undefined> = []
     * const filteredArr = myArr.filter(Filters.nonnull)
     * //    ^ Array<string>
     * ``` */
    export const nonnull = <T>(x: T): x is Exclude<T, undefined | null> => x != null;
    /** A higher order function that creates a filter function based on the passed `union`
     * The filter function returns `true` if `union.includes(x)`
     *
     * ### Example
     * ```typescript
     * const myArr = ['foo', 'bar', 'baz', 'hello', 'world']
     * //    ^ Array<string>
     *
     * const filteredArr = myArr.filter(Filters.inUnion(['hello', 'world']))
     * //    ^ Array<'hello' | 'world'>
     * ``` */
    export const inUnion =
        <T>(union: readonly T[]) =>
        (x: any): x is T =>
            union.includes(x);

    /** Filter by boolean casting */
    export const ok = <T>(x: T): x is Exclude<T, undefined | null | false | 0 | ''> => !!x;

    /** A filter that only returns the unique values via `Object.is()` */
    export const unique = (value: unknown, index: number, array: unknown[]): boolean =>
        array.indexOf(value) === index;

    export function require<T extends Record<string, unknown>, K extends keyof T>(key: K) {
        return (value: T): value is ts.Object.require<T, K> =>
            value[key] !== undefined && value[key] !== null;
    }
}
